import React from 'react'
// import './segment-list.scss'

import { Container } from 'typescript-ioc/es5'
import classnames from 'classnames'
import clone from 'clone'
import * as moment from 'moment-timezone'
import deepEqual from 'react-fast-compare'
import { AntdTableEmptyPlaceholder, Well } from '@pushly/aqe/lib/components'

import SegmentListHeader from './segment-list-header'
import { SegmentListState } from './interfaces'
import { SegmentListContext } from './segment-list-context'
import { AppState } from '../../stores/app'
import { AccountService, AppService, DomainService } from '../../services'
import { TablePaginationConfig } from 'antd/lib/table'
import { getTablePaginationConfig } from './helpers'
import { Key, SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface'
import { InfoCircleOutlined, LoadingOutlined, TeamOutlined } from '@ant-design/icons'
import { stripUndefined } from '../../_utils/strip-undefined'
import { tryParseInt } from '../../_utils/try-parse'
import { getQueryStringParam } from '../../_utils/get-query-string'
import { SizeType } from 'antd/lib/config-provider/SizeContext'
import { onAccountIdChange } from '../../_utils/account'
import { OrgSegmentService } from '../../services/org-segment'
import { OrgSegmentModel } from '../../models/segments/org-segment.model'
import { simpleNotification } from '../../_utils/utils'
import { INestedBatchRequest, INestedBatchResponse } from '../../interfaces/batch-requests'
import { BatchService } from '../../services/batch'
import { onDomainIdChange } from '../../_utils/domain'
import { SegmentModel } from '../../models/segments/segment.model'
import { SegmentService } from '../../services/segment'
import { SegmentSource } from '@pushly/models/lib/enums'
import {
    FEAT_CHANNEL_ANDROID,
    FEAT_CHANNEL_IOS,
    FEAT_INLINE_SEG,
    IMMUTABLE_FLAG,
    TZ_PLATFORM_DEFAULT,
} from '../../constants'
import { AbilityAction } from '../../enums/ability-action.enum'
import { SubjectEntity } from '../../enums/ability-entity.enum'
import { asCaslSubject } from '../../stores/app-ability'
import SwSimpleLinkPagination from '../sw-simple-link-pagination/sw-simple-link-pagination'
import { IApiResponsePaginationLinks } from '../../interfaces/api-response-pagination-links'
import { determineChannelBadge } from '../badges/_utils'
import { RouteComponentProps } from 'react-router-dom'
import { SegmentType } from '@pushly/models/lib/enums/segment-type'
import { DeliveryChannel } from '@pushly/models/lib/enums'
import { OrgSegmentRowReachBreakdown, SegmentRowActions, SegmentRowDisplay, SegmentRowReach } from './row-components'
import { SegmentRemoteReachApi } from '../../hooks/use-segment-remote-reach'
import { Modal, Popover, Table, Space } from 'antd'

type FilterKey = keyof SegmentListState['filters']

const COLUMN_KEYS = {
    name: {
        org: 'segment_group.name',
        domain: 'segment.name',
    },
    'last-targeted': {
        org: 'segment_group.last_targeted_date_utc',
        domain: 'segment.last_targeted_date_utc',
    },
    'created-at': {
        org: 'segment_group.created_at',
        domain: 'segment.created_at',
    },
    channels: {
        domain: 'segment.channels',
        org: 'segment_group.channels',
    },
}

function getColumnKey(level: 'org' | 'domain', alias: keyof typeof COLUMN_KEYS): string {
    return COLUMN_KEYS[alias][level]
}

interface IOrgSegmentListProps {
    level: 'org'
    orgId: number
}

interface IDomainSegmentListProps {
    level: 'domain'
    domainId: number
}

type SegmentListProps = Partial<RouteComponentProps<any>> &
    (IOrgSegmentListProps | IDomainSegmentListProps) & {
        title?: string
        filterSize?: SizeType
        filtersAddonBefore?: React.ReactNode
        filtersAddonAfter?: React.ReactNode
        pageSize?: number
        hidePagination?: boolean
        hideRowActions?: boolean
        hideRefresh?: boolean
        hideFilters?: FilterKey | FilterKey[] | boolean
        filtersDisabled?: FilterKey | FilterKey[] | boolean
        defaultFilters?: {
            search?: string
        }
    }

type State = SegmentListState & {
    dataSourcePreviousQuery?: any
    showFailedDialog: boolean
    failedBulkResponses?: INestedBatchResponse[]
    failedBulkRetryAction?: () => any
    failedBulkMessage?: string
    processingBulkOperation: boolean
    paginationLinks?: IApiResponsePaginationLinks
}

type TabbedViewProps = SegmentListProps & {
    isActiveTab: boolean
}

type RemoteReachApiRefMap = Map<number, React.MutableRefObject<SegmentRemoteReachApi | null>>

class SegmentList extends React.Component<SegmentListProps, State> {
    protected readonly appState: AppState
    protected readonly appSvc: AppService
    protected readonly orgSvc: AccountService
    protected readonly domainSvc: DomainService
    protected readonly orgSegmentSvc: OrgSegmentService
    protected readonly segmentSvc: SegmentService
    protected readonly batchSvc: BatchService

    protected unmounting: boolean = false
    protected disposeObservers: (() => void)[] = []
    protected remoteReachRefs: RemoteReachApiRefMap = new Map()

    public constructor(props: SegmentListProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appSvc = Container.get(AppService)
        this.orgSvc = Container.get(AccountService)
        this.domainSvc = Container.get(DomainService)
        this.orgSegmentSvc = Container.get(OrgSegmentService)
        this.segmentSvc = Container.get(SegmentService)
        this.batchSvc = Container.get(BatchService)

        // initialize state with defaults from path/query
        this.state = this.applyRequestDefaults({
            level: this.props.level,
            levelDependenciesLoaded: false,
            org: null!, // loaded on mount
            domain: null!, // loaded on mount

            dataSourceLoaded: false,
            dataSource: [],

            refreshing: false,
            refreshEnabled: true,

            sortedInfo: {
                columnKey: getColumnKey(this.props.level, 'name'),
                order: 'ascend',
            },
            paginationConfig: getTablePaginationConfig({
                pageSize: this.props.pageSize ?? 20,
            }),
            selectedIds: [],
            filters: {
                search: this.props.defaultFilters?.search,
                source: [SegmentSource.STANDARD],
                showAllSegments: this.props.level !== 'org',
                channels: [DeliveryChannel.WEB, DeliveryChannel.NATIVE_IOS, DeliveryChannel.NATIVE_ANDROID],
            },

            showFailedDialog: false,
            processingBulkOperation: false,
        })
    }

    public async componentDidMount() {
        let entityIdChangeHandler = this.props.level === 'domain' ? onDomainIdChange : onAccountIdChange
        this.disposeObservers.push(
            entityIdChangeHandler(this.appState, async () => {
                await this.fetchOrganizationalDependencies()
                this.resetList(true)
            }),
        )

        await this.fetchOrganizationalDependencies()
        this.fetchSegments()
    }

    public componentWillUnmount() {
        this.unmounting = true
        this.disposeObservers.forEach((fn) => fn())
    }

    public render() {
        const {
            levelDependenciesLoaded,
            dataSourceLoaded,
            refreshing,
            processingBulkOperation,
            showFailedDialog,
            failedBulkResponses,
        } = this.state

        const currDataSource: (OrgSegmentModel | SegmentModel)[] = this.state.dataSource
        const showTableLoadingAnimation =
            !levelDependenciesLoaded || (!dataSourceLoaded && !refreshing) || processingBulkOperation

        const filtersHidden: FilterKey[] = !this.props.hideFilters
            ? []
            : this.props.hideFilters === true
            ? ['search', 'source', 'showAllSegments']
            : Array.isArray(this.props.hideFilters)
            ? this.props.hideFilters
            : [this.props.hideFilters]

        const forceHideShowAllFilter = this.props.level === 'domain'
        if (!filtersHidden.includes('showAllSegments') && forceHideShowAllFilter) {
            filtersHidden.push('showAllSegments')
        }

        let viewHasNativePush

        const nativeIOSFlag = this.appState.flags.findActive(FEAT_CHANNEL_IOS)?.getKey()
        const nativeAndroidFlag = this.appState.flags.findActive(FEAT_CHANNEL_ANDROID)?.getKey()
        if (this.props.level === 'domain') {
            const domain = this.appState.currentDomain!
            const domainHasNativeIOS = domain.flags.includes(nativeIOSFlag!)
            const domainHasNativeAndroid = domain.flags.includes(nativeAndroidFlag!)

            viewHasNativePush =
                (domainHasNativeIOS && domain.nativeApnsConfiguration?.isActive) ||
                (domainHasNativeAndroid && domain.nativeFcmConfiguration?.isActive)
        } else {
            const accountFlags = this.appState.currentDomain?.accountFlags
            const accountHasNativeIOS = accountFlags?.includes(nativeIOSFlag!)
            const accountHasNativeAndroid = accountFlags?.includes(nativeAndroidFlag!)

            viewHasNativePush = accountHasNativeIOS || accountHasNativeAndroid
        }

        if (!viewHasNativePush) {
            filtersHidden.push('channels')
        }

        const visibleIds = currDataSource.map((segment) => segment.getId())
        const selectedVisibleIds = this.state.selectedIds.filter((id) => visibleIds.includes(id))

        return !this.canExecute ? null : (
            <SegmentListContext.Provider
                value={{
                    ...this.state,
                    onSearchClick: this.handleSearchClick,
                    onFilterChange: this.handleFilterChange,
                    onRefreshClick: this.handleRefreshClick,
                    onRefreshEnabledChange: this.handleRefreshEnabledChange,
                }}
            >
                <Table
                    className={classnames('segment-list-table', 'aqe-table-well', {
                        'hide-title': !this.props.title,
                        'hide-pagination': this.props.hidePagination,
                        'hide-actions': this.props.level === 'org' && !this.currentUserHasWriteAccess,
                    })}
                    rowClassName="segment-list-row"
                    size="small"
                    dataSource={currDataSource}
                    rowKey={(r) => r.getId()}
                    pagination={false}
                    title={() => (
                        <SegmentListHeader
                            title={this.props.title}
                            filterSize={this.props.filterSize}
                            filtersAddonBefore={this.props.filtersAddonBefore}
                            filtersAddonAfter={this.props.filtersAddonAfter}
                            hideFilters={filtersHidden}
                            hideRefresh={this.props.hideRefresh}
                            filtersDisabled={this.props.filtersDisabled}
                            onArchiveClick={this.handleBulkArchive}
                            onAddDomains={this.handleBulkAddDomains}
                        />
                    )}
                    footer={
                        this.props.hidePagination
                            ? undefined
                            : (currentTableData) => (
                                  <SwSimpleLinkPagination
                                      currentTableData={currentTableData}
                                      links={this.state.paginationLinks}
                                      onPrev={async (_ev, _link, config) => {
                                          await this.setCurrentPage(config.current)
                                          await this.fetchSegments()
                                      }}
                                      onNext={async (_ev, _link, config) => {
                                          await this.setCurrentPage(config.current)
                                          await this.fetchSegments()
                                      }}
                                  />
                              )
                    }
                    onChange={this.handleTableChange}
                    rowSelection={
                        !this.appState.abilityStore.can(AbilityAction.UPDATE, SubjectEntity.SEGMENT)
                            ? undefined
                            : {
                                  selectedRowKeys: selectedVisibleIds,
                                  getCheckboxProps: (record: OrgSegmentModel | SegmentModel) =>
                                      ({
                                          'aria-label': `Select ${record.getName?.() || record.getId()}`,
                                      }) as any,
                                  columnTitle: (originalNode: React.ReactNode) => (
                                      <span aria-label="Select all rows">{originalNode}</span>
                                  ),
                                  onChange: (_selectedKeys: React.Key[], selectedRows: OrgSegmentModel[]) => {
                                      const nextSelectedIds = selectedRows.map((s) => s.getId())

                                      this.setState(({ selectedIds }) => {
                                          const newIds = nextSelectedIds
                                              .filter((id) => !selectedIds.includes(id as number))
                                              .map((id) => parseInt(id.toString(), 10))

                                          return {
                                              selectedIds: [
                                                  ...selectedIds.filter(
                                                      (id) => nextSelectedIds.includes(id) || !visibleIds.includes(id),
                                                  ),
                                                  ...newIds,
                                              ],
                                          }
                                      })
                                  },
                              }
                    }
                    locale={{
                        emptyText: <AntdTableEmptyPlaceholder text="No Segments" icon={<TeamOutlined />} />,
                    }}
                    loading={
                        showTableLoadingAnimation && {
                            indicator: <LoadingOutlined spin={true} />,
                        }
                    }
                >
                    <Table.Column
                        key={getColumnKey(this.props.level, 'name')}
                        sorter={true}
                        defaultSortOrder="ascend"
                        className="segment"
                        dataIndex={['id']}
                        title="Name"
                        render={(_, segment: OrgSegmentModel | SegmentModel) => (
                            <SegmentRowDisplay
                                level={this.props.level}
                                segment={segment}
                                canMutate={this.canMutate()}
                                canEditOrgSegment={
                                    segment instanceof OrgSegmentModel ? this.canEditOrgSegment(segment) : false
                                }
                            />
                        )}
                    />

                    <Table.Column
                        className="estimated-reach"
                        dataIndex={['estimatedReach']}
                        align="right"
                        title={
                            <React.Fragment>
                                <span>Audience Size </span>
                                <Popover
                                    overlayClassName="segment-list-estimated-reach-popover"
                                    trigger="hover"
                                    content={
                                        <span>
                                            Subscriber audience size is updated once a day at midnight in your domain's
                                            time zone. Real-time subscriber size can be obtained by viewing the audience
                                            size on the segment page.
                                            <br />
                                            <br />A value of -- indicates that audience size has yet to be computed for
                                            this segment but will be available shortly.
                                        </span>
                                    }
                                >
                                    <InfoCircleOutlined className="info-icon" />
                                </Popover>
                            </React.Fragment>
                        }
                        render={(reach, segment: OrgSegmentModel | SegmentModel) => (
                            <SegmentRowReach
                                level={this.props.level}
                                segment={segment}
                                reloadReachRef={this.getRemoteReachRef(segment.getId())}
                            />
                        )}
                        width={140}
                    />

                    {this.props.level === 'org' && (
                        <Table.Column
                            sorter={false}
                            className="reach-breakdown"
                            dataIndex={['estimatedReach']}
                            title="Domains"
                            align="right"
                            render={(_, segment: OrgSegmentModel) => <OrgSegmentRowReachBreakdown segment={segment} />}
                            width={120}
                        />
                    )}

                    <Table.Column
                        key={getColumnKey(this.props.level, 'channels')}
                        sorter={false}
                        className="delivery-channel"
                        dataIndex={['channels']}
                        title="Delivery Channels"
                        align="center"
                        render={(channels: DeliveryChannel[]) => (
                            <Space>
                                {channels
                                    .filter((val) => (!viewHasNativePush ? val === DeliveryChannel.WEB : true))
                                    .map((ch) => (
                                        <span key={ch}>{determineChannelBadge(ch)}</span>
                                    ))}
                            </Space>
                        )}
                        width={150}
                    />

                    {this.props.level === 'domain' && (
                        <Table.Column
                            key={getColumnKey(this.props.level, 'last-targeted')}
                            sorter={true}
                            className="last-targeted"
                            dataIndex={['lastTargetedDateUtc']}
                            title="Last Targeted"
                            align="right"
                            render={(lastTargetedDateUtc) => {
                                const levelEntity = this.state.level === 'org' ? this.state.org : this.state.domain
                                const tz = levelEntity.timezone ?? TZ_PLATFORM_DEFAULT

                                const dt = moment.tz(lastTargetedDateUtc, tz)
                                return !lastTargetedDateUtc ? '--' : dt.format('LL')
                            }}
                            width={150}
                        />
                    )}

                    <Table.Column
                        key="actions"
                        className="actions"
                        title="Actions"
                        align="right"
                        render={(_, segment: OrgSegmentModel | SegmentModel) => (
                            <SegmentRowActions
                                level={this.props.level}
                                segment={segment}
                                canMutate={this.canMutate()}
                                canEditOrgSegment={
                                    segment instanceof OrgSegmentModel ? this.canEditOrgSegment(segment) : false
                                }
                                currentUserHasWriteAccess={this.currentUserHasWriteAccess}
                                hideRowActions={this.props.hideRowActions ?? false}
                                onArchived={() => {
                                    this.fetchSegments(true)
                                }}
                            />
                        )}
                        width={120}
                    />
                </Table>

                <Modal
                    open={showFailedDialog}
                    className="segment-list-failed-bulk-dialog"
                    title="Failed Bulk Actions"
                    okText="Retry"
                    onOk={this.handleFailedDialogOk}
                    confirmLoading={processingBulkOperation}
                    cancelText="Dismiss"
                    onCancel={this.handleFailedDialogDismiss}
                >
                    {!!failedBulkResponses && (
                        <div>
                            <p>
                                {failedBulkResponses.length} segment(s) {this.state.failedBulkMessage}. Please try again
                                or contact your account manager for more information.
                            </p>
                        </div>
                    )}
                </Modal>
            </SegmentListContext.Provider>
        )
    }

    protected getRemoteReachRef(segmentId: number) {
        if (!this.remoteReachRefs.has(segmentId)) {
            this.remoteReachRefs.set(segmentId, React.createRef<SegmentRemoteReachApi | null>())
        }
        return this.remoteReachRefs.get(segmentId)!
    }

    protected get canExecute(): boolean {
        const tabbedViewProps = this.props as TabbedViewProps
        return !('isActiveTab' in tabbedViewProps) || tabbedViewProps.isActiveTab
    }

    protected get currentUserHasWriteAccess(): boolean {
        let userHasWriteAccess
        if (this.props.level === 'domain') {
            // Can user write to segments at the domain level?
            userHasWriteAccess = this.appState.abilityStore.can(AbilityAction.CREATE, SubjectEntity.SEGMENT)
        } else {
            const orgId = this.props.orgId
            const accessibleDomains =
                this.appState.currentUserDomains?.filter((d) => d.accountId?.toString() === orgId.toString()) ?? []

            // Can user write to segments at the org level?
            userHasWriteAccess =
                this.appState.abilityStore.can(
                    AbilityAction.UPDATE,
                    this.appState.abilityStore.getOrgOwnedIdentityFor(SubjectEntity.ORG_SEGMENT),
                ) && accessibleDomains.length > 1
        }

        return userHasWriteAccess
    }

    protected getAbilityEntity() {
        return this.props.level === 'org' ? SubjectEntity.ORG_SEGMENT : SubjectEntity.SEGMENT
    }

    protected getAbilityConstraint() {
        return this.props.level === 'org' ? { accountId: this.props.orgId } : { domainId: this.props.domainId }
    }

    protected canMutate() {
        return this.appState.abilityStore.can(
            AbilityAction.UPDATE,
            asCaslSubject(this.getAbilityEntity(), this.getAbilityConstraint()),
        )
    }

    protected canEditOrgSegment(model: OrgSegmentModel) {
        return this.props.level === 'domain'
            ? false
            : !model
                  .getSegments()
                  .some(
                      (s) =>
                          !this.appState.abilityStore.can(
                              AbilityAction.UPDATE,
                              asCaslSubject(SubjectEntity.SEGMENT, { domainId: s.domain_id }),
                          ),
                  )
    }

    protected async setStateAsync<K extends keyof State>(
        state:
            | ((prevState: Readonly<State>, props: Readonly<IOrgSegmentListProps>) => Pick<State, K> | State | null)
            | (Pick<State, K> | State | null),
    ): Promise<void> {
        return new Promise((res) => {
            if (!this.unmounting) {
                this.setState(state as State, res)
            }
        })
    }

    protected applyRequestDefaults(state: State): State {
        // default query
        const query = getQueryStringParam('query')
        if (!!query?.trim()) {
            state.filters = {
                ...state.filters,
                search: query.trim(),
            }
        }

        // ensure default page
        const currentPage = getQueryStringParam('page', tryParseInt)
        if (currentPage && currentPage !== 1) {
            state.paginationConfig = {
                ...state.paginationConfig,
                current: currentPage,
            }
        }

        return state
    }

    protected updatePathParams(): void {
        const {
            paginationConfig: { current: page },
        } = { ...this.state }

        let parsedUpdate: string | undefined

        if (page !== 1) {
            parsedUpdate = `&page=${page}`
        }

        this.props.history?.push?.({ search: parsedUpdate?.replace(/^&/, '') })
    }

    protected async resetList(clearAll?: boolean) {
        await this.setStateAsync({ dataSourceLoaded: false })

        const filtersOverride: any = {}
        if (clearAll) {
            filtersOverride.search = ''
        }

        await this.setStateAsync((curr) => ({
            selectedIds: [],
            paginationConfig: {
                ...curr.paginationConfig,
                current: 1,
            },
            filters: {
                ...curr.filters,
                ...filtersOverride,
            },
        }))

        this.fetchSegments(clearAll)
    }

    protected async setCurrentPage(page: number | undefined): Promise<void> {
        await this.setStateAsync(({ paginationConfig }) => ({
            paginationConfig: {
                ...paginationConfig,
                current: page ?? 1,
            },
        }))

        this.updatePathParams()
    }

    protected handleTableChange = async (
        pagination: TablePaginationConfig,
        _filters: Record<string, Key[] | null>,
        sorter: SorterResult<OrgSegmentModel | SegmentModel>,
        _extra: TableCurrentDataSource<OrgSegmentModel | SegmentModel>,
    ): Promise<void> => {
        let resolver = (cb: () => void) => cb()

        const sortedInfoData = { order: sorter.order, columnKey: sorter.columnKey }
        const sortChanged = !deepEqual(this.state.sortedInfo, sortedInfoData)
        if (sortChanged) {
            // ensure sorted info updated first
            resolver = (cb: () => void) => {
                this.setState({ sortedInfo: sortedInfoData }, () => cb())
            }
        }

        resolver(async () => {
            await this.setCurrentPage(pagination.current)
            this.fetchSegments()
        })
    }

    protected handleSearchClick = async (value: string) => {
        const prevFilter = this.state.filters.search

        await this.setStateAsync(({ filters }) => ({
            filters: {
                ...filters,
                search: value,
            },
        }))

        const emptyValues = !value.trim() && !prevFilter?.trim()
        if (!emptyValues) {
            await this.resetList()
            this.fetchSegments(true)
        }
    }

    protected handleFilterChange = async <FilterValue extends SegmentListState['filters'][FilterKey]>(
        key: FilterKey,
        value: FilterValue,
    ): Promise<void> => {
        const prevFilters = clone(this.state.filters)

        await this.setStateAsync(({ filters }) => ({
            filters: {
                ...filters,
                [key]: value,
            },
        }))

        if (!deepEqual(this.state.filters, prevFilters)) {
            await this.resetList()
            this.fetchSegments()
        }
    }

    protected handleRefreshClick = async (_ev: React.MouseEvent<HTMLElement>): Promise<void> => {
        // manual refresh click should not set refreshing
        // to ensure full table loading animation is shown
        this.fetchSegments(true)
    }

    protected handleRefreshEnabledChange = async (refreshEnabled: boolean) => {
        await this.setStateAsync({ refreshEnabled })
    }

    protected handleFailedDialogOk = async () => {
        return this.state.failedBulkRetryAction?.()
    }

    protected handleFailedDialogDismiss = () => {
        return this.setState({ showFailedDialog: false })
    }

    protected handleBulkArchive = async () => {
        const state: any = { processingBulkOperation: true }
        this.setState({ ...state })

        let segmentsPath
        if (this.props.level === 'domain') {
            segmentsPath = `/domains/${this.props.domainId}/segments`
        } else {
            segmentsPath = `/accounts/${this.props.orgId}/segments`
        }

        const buildArchiveReq = (id: number): INestedBatchRequest => ({
            meta: {
                segmentId: id,
            },
            method: 'DELETE',
            relative_path: `${segmentsPath}/${id}`,
        })

        const failedBulkResponses: INestedBatchResponse[] = []
        const selectedIds = this.state.selectedIds
        const idsToArchive = Array.from(new Set(selectedIds))

        const res = await this.batchSvc.batch({
            requests: idsToArchive.map(buildArchiveReq),
        })

        if (res.ok && res.data) {
            res.data.forEach((batchRes) => {
                if (batchRes.code !== 200) {
                    failedBulkResponses.push(batchRes)
                } else {
                    const checkedIdx = selectedIds.findIndex(
                        (id) => id.toString() === batchRes.meta.segment_id.toString(),
                    )
                    if (checkedIdx !== -1) {
                        selectedIds.splice(checkedIdx, 1)
                    }
                }
            })
        }

        state.processingBulkOperation = false
        state.selectedDomainIds = selectedIds
        state.failedBulkResponses = failedBulkResponses
        state.showFailedDialog = failedBulkResponses.length > 0

        if (!state.showFailedDialog) {
            simpleNotification('success', `${idsToArchive.length} Segments successfully archived.`)
            state.selectedDomainIds = []
        } else {
            state.failedBulkRetryAction = this.handleBulkArchive
            state.failedBulkMessage = 'failed to archive'
        }

        this.setState(state)
        this.fetchSegments(true)
    }

    protected handleBulkAddDomains = async (domainIds: number[]) => {
        // this method is org level only
        const props = this.props as IOrgSegmentListProps

        const state: any = { processingBulkOperation: true }
        this.setState({ ...state })

        const failedBulkResponses: INestedBatchResponse[] = []
        const selectedIds = this.state.selectedIds
        const idsToUpdate = Array.from(new Set(selectedIds))

        const buildAddReq = (id: number): INestedBatchRequest => ({
            method: 'POST',
            relative_path: `/accounts/${props.orgId}/segments/${id}/domains`,
            body: { domainIds },
            meta: {
                segmentId: id,
            },
        })

        const res = await this.batchSvc.batch({
            requests: idsToUpdate.map(buildAddReq),
        })

        if (res.ok && res.data) {
            res.data.forEach((batchRes) => {
                if (batchRes.code < 200 || batchRes.code >= 300) {
                    failedBulkResponses.push(batchRes)
                } else {
                    const checkedIdx = selectedIds.findIndex(
                        (id) => id.toString() === batchRes.meta.segment_id.toString(),
                    )
                    if (checkedIdx !== -1) {
                        selectedIds.splice(checkedIdx, 1)
                    }
                }
            })
        }

        state.processingBulkOperation = false
        state.selectedDomainIds = selectedIds
        state.failedBulkResponses = failedBulkResponses
        state.showFailedDialog = failedBulkResponses.length > 0

        if (!state.showFailedDialog) {
            simpleNotification('success', `${idsToUpdate.length} Segments successfully updated.`)
            state.selectedDomainIds = []
        } else {
            state.failedBulkRetryAction = () => this.handleBulkAddDomains(domainIds)
            state.failedBulkMessage = 'could not be updated'
        }

        this.setState(state)
        this.fetchSegments(true)
    }

    protected async fetchSegments(forceFetch?: boolean) {
        if (!this.canExecute) {
            return
        }

        const { filters } = this.state

        const defaultSortInfo = { columnKey: getColumnKey(this.props.level, 'name'), order: 'asc' }
        const sortedInfo = this.state.sortedInfo ?? defaultSortInfo
        const orderConfig: any = {
            field: sortedInfo.columnKey,
            direction: sortedInfo.order,
            nulls: 'LAST',
        }

        const apiOptions = {
            showLoadingScreen: false,
            cancellationKey: 'segment-list.fetch',
            query: stripUndefined({
                page: this.state.paginationConfig.current,
                limit: this.state.paginationConfig.pageSize,
                include_default: false,
                order: JSON.stringify(orderConfig),
                source: [SegmentSource.STANDARD],
                search: filters.search ?? undefined,
                multi_domain_only: !filters.showAllSegments,
                channels: filters.channels.join(','),
                types: SegmentType.STANDARD,
            }),
        }

        // Domain level source filtering
        const inlineSegFlag = this.appState.flags.findActive(FEAT_INLINE_SEG)?.getKey()
        const hasInlineSegFeat =
            this.props.level === 'domain' &&
            'domain' in this.state &&
            !!inlineSegFlag &&
            this.state.domain?.flags?.includes(inlineSegFlag)
        if (hasInlineSegFeat && this.state.filters.source) {
            apiOptions.query.source = this.state.filters.source.join(',')
        }

        // Abort fetch if manual refresh and query has not changed
        const sameQuery =
            !this.state.refreshing &&
            this.state.dataSourcePreviousQuery &&
            deepEqual(apiOptions.query, this.state.dataSourcePreviousQuery)
        if (!forceFetch && sameQuery) {
            return
        }

        // Start loading animation
        await this.setStateAsync({
            dataSourceLoaded: false,
            dataSourcePreviousQuery: apiOptions.query,
        })

        let nextDataSource: (OrgSegmentModel | SegmentModel)[] = []
        const state: any = {
            ...this.state,
            refreshing: false,
            dataSourceLoaded: true,
        }

        let resolver
        if (this.props.level === 'domain') {
            resolver = this.domainSvc.fetchSegmentsByDomainId(this.props.domainId, apiOptions)
        } else {
            resolver = this.orgSegmentSvc.fetchAll(this.props.orgId, apiOptions)
        }

        const res = await Promise.resolve(resolver)
        if (!res.cancelled) {
            if (res.ok && res.data) {
                let modelKlass = this.props.level === 'domain' ? SegmentModel : OrgSegmentModel
                res.data.forEach((datum) => nextDataSource.push(modelKlass.build(datum)))

                state.paginationLinks = res.meta.links ?? state.paginationLinks
            } else if (res.error) {
                console.warn(res.error)
            }

            if (this.props.level === 'domain') {
                nextDataSource = nextDataSource.filter((s: SegmentModel) => !s.getFlags().includes(IMMUTABLE_FLAG))
            }

            state.dataSource = nextDataSource
            this.dispatchRemoteReachRefresh(nextDataSource)

            await this.setStateAsync(state)
        }
    }

    // Cycle through fetched segment ids and either:
    // A. Add new reference for new rows
    // B. Use existing row reference to refresh remote reach
    protected dispatchRemoteReachRefresh(segments: (SegmentModel | OrgSegmentModel)[]) {
        const unusedRemoteApiRefIds = new Map(this.remoteReachRefs.entries())

        for (const segment of segments) {
            const segmentId = segment.getId()

            const currRemoteApiRef = this.remoteReachRefs.get(segmentId)
            if (currRemoteApiRef?.current) {
                unusedRemoteApiRefIds.delete(segmentId)
                currRemoteApiRef.current.reload().catch(() => {
                    console.warn(`Failed to dispatch remote reach reload for segment ${segmentId}.`)
                })
            } else if (!currRemoteApiRef) {
                this.remoteReachRefs.set(segmentId, React.createRef<SegmentRemoteReachApi | null>())
            }
        }

        // Clean up old references
        Array.from(unusedRemoteApiRefIds.keys()).forEach((key) => {
            this.remoteReachRefs.delete(key)
        })
    }

    protected async fetchOrganizationalDependencies() {
        if (!this.canExecute) {
            return
        }

        const state: any = { levelDependenciesLoaded: false }
        await this.setStateAsync({ ...state })

        if (this.props.level === 'domain') {
            const { ok, data } = await this.domainSvc.fetchById(this.props.domainId)
            if (ok && data) {
                state.domain = data
            }
        } else {
            state.org = await this.orgSvc.fetchById(this.props.orgId)
        }

        state.levelDependenciesLoaded = true
        await this.setStateAsync(state)
    }
}

export default SegmentList
